import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { api as platformStackApi } from '@sierralabs/core-ui';

import { useGetJiraConnect } from './hooks/useGetJiraConnect';
import { useGetJiraApi } from './JiraConnect.hooks';
import {
  JiraApiProperties,
  JiraConnectAPI,
  JiraIssueField
} from './JiraConnect.interface';

export interface JiraConnectContextState {
  token?: string;
  connect?: JiraConnectAPI;
  isAvailable: boolean;
  getJiraApi: () => void;
  getSchemaKey: (issueField: JiraIssueField) => string | undefined;
  getSchemaProperties: (
    keyOrIssueField: JiraIssueField | string
  ) => JiraApiProperties | undefined;
}

export const JiraConnectContext = createContext<JiraConnectContextState>({
  isAvailable: false,
  getJiraApi: () => {},
  getSchemaKey: () => undefined,
  getSchemaProperties: () => undefined
});

export function useJiraConnectContext(): JiraConnectContextState {
  const context: JiraConnectContextState = useContext(JiraConnectContext);
  if (!context) {
    throw new Error(
      'useJiraConnectContext must be used within the JiraConnectProvider component.'
    );
  }
  return context;
}

// NOTE: return values come from Jira OpenApi JSON Schema (useGetJiraApi)
function applySpecialCases(propertyName: string): string {
  switch (propertyName.toLowerCase()) {
    case 'component':
    case 'components':
      return 'ProjectComponent';
    case 'issuelink':
    case 'issuelinks':
      return 'IssueLink';
    case 'issuetype':
    case 'issuetypes':
      return 'IssueTypeDetails';
    case 'timetracking':
      return 'TimeTrackingDetails';
    case 'watch':
    case 'watches':
      return 'Watchers';
  }
  return propertyName;
}

export const JiraConnectProvider: React.FC = ({ children }) => {
  const didFetchRef = useRef<boolean>(false);
  const [token, setToken] = useState<string | undefined>();
  const connect = useGetJiraConnect();
  const [{ data: jiraApi }, fetchJiraApi] = useGetJiraApi();
  const isAvailable = !!connect;

  // fetch once
  const getJiraApi = useCallback(() => {
    !didFetchRef.current && fetchJiraApi();
  }, [fetchJiraApi]);

  const getSchemaKey = useCallback(
    (issueField: JiraIssueField): string | undefined => {
      if (!jiraApi) {
        return;
      }
      const { schema } = issueField;
      const {
        type = '',
        items = '',
        system = '',
        custom,
        customId
      } = schema || {};
      const isCustom: boolean = !!(custom || customId);
      const names: string[] = [issueField.name, issueField.id];
      type && names.push(type);
      items && names.push(items);
      system && names.push(system);

      // add CustomField for custom properties
      const matches: string[] = [];
      for (const name of names) {
        const prefix: string = isCustom ? 'CustomField' : '';
        const property: string = applySpecialCases(name);
        const propertyWithPrefix: string = prefix.concat(property);
        matches.push(property.toLowerCase(), propertyWithPrefix.toLowerCase());
      }

      const schemaKeys: string[] = Object.keys(jiraApi.components.schemas);
      return schemaKeys.find((key: string) => {
        return matches.includes(key.toLowerCase());
      });
    },
    [jiraApi]
  );

  const getSchemaProperties = useCallback(
    (
      keyOrIssueField: JiraIssueField | string
    ): JiraApiProperties | undefined => {
      if (!jiraApi) {
        return;
      }
      const key: string | undefined =
        typeof keyOrIssueField === 'string'
          ? keyOrIssueField
          : getSchemaKey(keyOrIssueField);
      if (key) {
        return jiraApi.components.schemas[key]?.properties;
      }
    },
    [jiraApi, getSchemaKey]
  );

  useEffect(() => {
    didFetchRef.current = !!jiraApi;
  }, [jiraApi]);

  useEffect(() => {
    if (!connect) {
      return;
    }

    // ensure that the iframe does not gradually increase height by fixing
    // the height to take the entire page and let the app handle the scrolling
    connect.sizeToParent(true);

    connect.context.getToken((token: string) => {
      setToken(token);

      // set height again in case the jira iframe scrollbar is still visible
      connect.sizeToParent(true);
    });
  }, [connect]);

  useEffect(() => {
    // set token to environment so api.fetch() for v2 will include Jira-Authorization header
    const key = 'Jira-Authorization';
    if (!platformStackApi.environment.headers) {
      platformStackApi.environment.headers = {};
    }
    if (token) {
      platformStackApi.environment.headers[key] = `JWT ${token}`;
    }
    return () => {
      // remove headers
      if (platformStackApi.environment.headers) {
        delete platformStackApi.environment.headers[key];
      }
    };
  }, [token]);

  return (
    <JiraConnectContext.Provider
      value={{
        token,
        connect,
        isAvailable,
        getJiraApi,
        getSchemaKey,
        getSchemaProperties
      }}
    >
      {children}
    </JiraConnectContext.Provider>
  );
};
